import { useRef } from 'react';
import {
  downloadAttachment,
  useAttachments,
  useDeleteAttachment,
  useUploadAttachment,
} from '../../lib/api/attachments';
import { Attachment, MAX_ATTACHMENT_SIZE_BYTES } from '../../lib/types';
import { Icon } from '../ui/Icon';
import { useToast } from '../ui/Toast';

/** Compact inline row of attachment pills + an Add button.
 *  Used in the business-card header on Brand / Campaign detail pages. */
export const AttachmentsRow = ({
  campaignId,
  brandId,
  taskId,
}: { campaignId?: string; brandId?: string; taskId?: string }) => {
  const filter: { campaign?: string; brand?: string; task?: string } = campaignId
    ? { campaign: campaignId }
    : brandId
      ? { brand: brandId }
      : { task: taskId };
  const { data: items = [] } = useAttachments(filter);
  const upload = useUploadAttachment();
  const del = useDeleteAttachment();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        toast.push(`"${file.name}" is too large (max 25 MB)`);
        continue;
      }
      try {
        await upload.mutateAsync({
          file,
          campaign: campaignId,
          brand: brandId,
          task: taskId,
        });
      } catch {
        toast.push(`Could not upload ${file.name}`);
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const onDownload = async (att: Attachment) => {
    try {
      await downloadAttachment(att);
    } catch {
      toast.push('Could not download');
    }
  };

  const onDelete = async (att: Attachment) => {
    if (!window.confirm(`Delete "${att.name}"?`)) return;
    try {
      await del.mutateAsync(att.id);
    } catch {
      toast.push('Could not delete');
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: 6,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}
    >
      {items.map((att) => (
        <span
          key={att.id}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            background: 'transparent',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-md)',
            padding: '3px 4px 3px 8px',
            fontSize: 11.5,
            maxWidth: 220,
          }}
        >
          <Icon name="archive" size={11} />
          <button
            type="button"
            onClick={() => onDownload(att)}
            title={`${att.name} — click to download`}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'var(--ink-2)',
              fontSize: 11.5,
              maxWidth: 160,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {att.name}
          </button>
          <button
            type="button"
            onClick={() => onDelete(att)}
            className="icon-btn"
            title="Delete"
            style={{ width: 18, height: 18, color: 'var(--ink-4)' }}
          >
            <Icon name="x" size={10} />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={upload.isPending}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          background: 'transparent',
          border: '1px dashed var(--border-strong)',
          borderRadius: 'var(--r-md)',
          padding: '3px 8px',
          fontSize: 11.5,
          color: 'var(--ink-3)',
          cursor: 'pointer',
        }}
      >
        <Icon name="plus" size={11} />
        {upload.isPending ? 'Uploading…' : items.length === 0 ? 'Add attachment' : 'Add'}
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
};

export default AttachmentsRow;
